// Sticky subnav header with jitter-free stuck detection.
//
// Convention:
//   <div id="subnav-sticky-sentinel"></div>
//   <div id="subnav-sticky-header" class="my-sticky-header">...</div>
//
// Use a CSS rule like .my-sticky-header.is-stuck to reduce padding/layout when
// stuck. This JS compensates for the resulting height change with a dynamic
// margin-bottom, preventing content from jumping when the header shrinks.
//
// Uses IntersectionObserver as the primary trigger, with a passive scroll
// listener as a fallback for edge cases (fast scroll at page load, browser
// quirks). Both call the same idempotent apply().
//
// The sentinel is a zero-height element whose visibility is unaffected by
// changes to the header's height, preventing feedback loops.
//
// Sets --subnav-header-height on <html> so other sticky elements (e.g. label
// preview sidebars) can position themselves below this header via CSS calc().
//
// Returns a cleanup function, or null if the elements aren't on the page.

const SENTINEL_ID = "subnav-sticky-sentinel";
const HEADER_ID = "subnav-sticky-header";

export function initStickySubnavHeader() {
  const sentinel = document.getElementById(SENTINEL_ID);
  const header = document.getElementById(HEADER_ID);
  if (!sentinel || !header) return null;

  let stuckHeight;
  let shrinkage;
  let width;
  let isStuck = false;

  // Toggling here won't flash: offsetHeight forces a reflow but not a repaint,
  // and the browser only paints once this JS task yields.
  const measure = () => {
    const wasStuck = header.classList.contains("is-stuck");
    header.classList.remove("is-stuck");
    const unstuckHeight = header.offsetHeight;
    header.classList.add("is-stuck");
    stuckHeight = header.offsetHeight;
    // Restoring the class keeps the header at its original size, so the resize
    // observer below does not fire.
    header.classList.toggle("is-stuck", wasStuck);
    shrinkage = unstuckHeight - stuckHeight;
    width = header.offsetWidth;
  };

  const write = () => {
    if (isStuck) {
      header.classList.add("is-stuck");
      header.style.marginBottom = shrinkage > 0 ? shrinkage + "px" : "";
      document.documentElement.style.setProperty("--subnav-header-height", stuckHeight + "px");
    } else {
      header.classList.remove("is-stuck");
      header.style.marginBottom = "";
      document.documentElement.style.setProperty("--subnav-header-height", "0px");
    }
  };

  const apply = (shouldBeStuck) => {
    if (shouldBeStuck === isStuck) return;
    isStuck = shouldBeStuck;
    write();
  };

  measure();

  // IntersectionObserver reports isIntersecting for a sentinel sitting exactly
  // on the viewport edge, so both triggers read the rect. See _docs/sticky-header-jitter.md.
  const isPastSentinel = () => sentinel.getBoundingClientRect().bottom < 0;

  // Primary: IntersectionObserver
  const observer = new IntersectionObserver(() => apply(isPastSentinel()), {
    threshold: 0,
  });
  observer.observe(sentinel);

  // Fallback: passive scroll listener catches cases the observer misses.
  const onScroll = () => {
    apply(isPastSentinel());
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  const resizeObserver = new ResizeObserver(() => {
    const expectedHeight = isStuck ? stuckHeight : stuckHeight + shrinkage;
    // Narrowing can rewrap the unstuck header while the stuck one keeps its
    // height, so the height alone would not show the new shrinkage.
    if (header.offsetWidth === width && header.offsetHeight === expectedHeight) return;
    measure();
    write();
  });
  resizeObserver.observe(header);

  return () => {
    observer.disconnect();
    resizeObserver.disconnect();
    window.removeEventListener("scroll", onScroll);
  };
}
